import { adminService } from 'shared/api'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  TextField,
  Tooltip,
  Typography,
  IconButton
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { GiftCardCreateModel } from 'shared/models'
import { yupResolver } from '@hookform/resolvers/yup'
import { getValidations } from './validations'
import { useState } from 'react'
import { IntlPhoneInput } from 'features/intl-phone-input'
import { giftCardView } from 'pages/nav'
import { useNavigate } from 'react-router-dom'
import { MessageTypes, showMessage } from 'store/slices/message-slice'
import { useAppDispatch } from 'store'
import CopyIcon from '@mui/icons-material/FileCopy'
import { PaymentLinkWrapper } from 'features/getting-payment-link-dialog/styled'

function GiftCardCreateForm(props: { onClose?: () => void }) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { handleSubmit, control } = useForm<GiftCardCreateModel>({
    resolver: yupResolver(getValidations()),
    defaultValues: { amount: undefined, email: '', name: '', phoneNumber: '', staffComment: '' }
  })

  const [isProcess, setProcess] = useState(false)

  const [result, setResult] = useState<{ id: number; paymentLink: string } | undefined>(undefined)

  async function handleSave(data: GiftCardCreateModel) {
    setProcess(true)

    try {
      const res = await adminService.giftCard.createGiftCard(data)

      if (res?.data?.id) {
        setResult(res.data)
      }
    } finally {
      setProcess(false)
    }
  }

  function handleLinkCopy(paymentLink: string) {
    return () => {
      navigator.clipboard.writeText(paymentLink)
      dispatch(showMessage({ text: 'Ссылка скопирована в буфер обмена', type: MessageTypes.Success }))
    }
  }

  return (
    <>
      {!!result && (
        <div>
          {!!result.paymentLink && (
            <DialogContent>
              <Typography fontSize="16px">Ссылка на оплату:</Typography>
              <PaymentLinkWrapper style={{ marginTop: "-0.5rem" }}>
                <Typography
                  component="a"
                  target="_blank"
                  href={result.paymentLink}
                  style={{ marginTop: '1rem', wordBreak: 'break-word' }}
                >
                  {result.paymentLink}
                </Typography>
                <Tooltip title="Скопировать в буфер обмена">
                  <IconButton size="small" onClick={handleLinkCopy(result.paymentLink)}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </PaymentLinkWrapper>
            </DialogContent>
          )}
          <Box style={{ position: 'sticky', bottom: 0 }} sx={{ bgcolor: 'background' }}>
            <Divider />
            <DialogActions>
              <Button variant="contained" disableElevation onClick={() => navigate(giftCardView(String(result.id)))}>
                К сертификату
              </Button>
              {props.onClose && (
                <Button variant="outlined" disableElevation onClick={props.onClose}>
                  Закрыть
                </Button>
              )}
            </DialogActions>
          </Box>
        </div>
      )}
      {!result && (
        <form onSubmit={handleSubmit(handleSave)}>
          <DialogContent>
            <Controller
              control={control}
              name="name"
              defaultValue=""
              render={({ field, fieldState }) => (
                <TextField
                  label="Имя"
                  fullWidth
                  placeholder="Имя"
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={field.disabled || isProcess}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  ref={field.ref}
                  margin="none"
                />
              )}
            />
            <Controller
              control={control}
              name="email"
              defaultValue=""
              render={({ field, fieldState }) => (
                <TextField
                  label="Email"
                  fullWidth
                  type="email"
                  autoComplete="off"
                  placeholder="Email"
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={isProcess}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  ref={field.ref}
                  margin="dense"
                />
              )}
            />
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field, fieldState }) => (
                <IntlPhoneInput
                  label="Контактный телефон"
                  fullWidth
                  placeholder="Контактный телефон"
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={field.disabled || isProcess}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  margin="dense"
                />
              )}
            />
            <Controller
              control={control}
              name="amount"
              render={({ field, fieldState }) => (
                <TextField
                  label="Сумма"
                  fullWidth
                  placeholder="Сумма"
                  type="number"
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  value={field.value ? String(field.value) : ''}
                  onChange={(ev) => field.onChange(ev.target.value ? Number(ev.target.value) : undefined)}
                  onBlur={field.onBlur}
                  disabled={field.disabled || isProcess}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  ref={field.ref}
                  margin="dense"
                />
              )}
            />
            <Controller
              control={control}
              name="staffComment"
              render={({ field, fieldState }) => (
                <TextField
                  label="Комментарий"
                  fullWidth
                  placeholder="Комментарий"
                  multiline
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={field.disabled || isProcess}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  ref={field.ref}
                  margin="dense"
                />
              )}
            />
          </DialogContent>
          <Box style={{ position: 'sticky', bottom: 0 }} sx={{ bgcolor: 'background' }}>
            <Divider />
            <DialogActions>
              <Button type="submit" disabled={isProcess} fullWidth variant="contained" disableElevation>
                Создать
              </Button>
            </DialogActions>
          </Box>
        </form>
      )}
    </>
  )
}

export { GiftCardCreateForm }
